(() => {
  const MAX_FILE_COUNT = 5;
  const MAX_TOTAL_BYTES = 20 * 1024 * 1024;
  const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
  const IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif']);

  function formatFileSize(bytes) {
    const size = Number(bytes) || 0;
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  function getExtension(fileName) {
    const parts = String(fileName || '').toLowerCase().split('.');
    return parts.length > 1 ? `.${parts.pop()}` : '';
  }

  function getFiles(form) {
    return Array.from(form.querySelectorAll('input[type="file"]'))
      .flatMap((input) => Array.from(input.files || []))
      .filter((file) => file && file.size > 0);
  }

  function getAllowedExtensions(form) {
    const configured = (form.dataset.allowedExtensions || '')
      .split(',')
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);
    return new Set(configured.length ? configured : ['.jpg', '.jpeg', '.png', '.webp']);
  }

  function validateFiles(form) {
    const files = getFiles(form);
    const allowedExtensions = getAllowedExtensions(form);
    const totalSize = files.reduce((sum, file) => sum + (file.size || 0), 0);

    if (files.length > MAX_FILE_COUNT) {
      return `Choose ${MAX_FILE_COUNT} or fewer files.`;
    }

    if (totalSize > MAX_TOTAL_BYTES) {
      return `Keep files under ${formatFileSize(MAX_TOTAL_BYTES)} total.`;
    }

    for (const file of files) {
      const extension = getExtension(file.name);
      if (!allowedExtensions.has(extension)) {
        return `${file.name || 'This file'} is not an accepted file type.`;
      }
      if (IMAGE_EXTENSIONS.has(extension) && file.size > MAX_IMAGE_BYTES) {
        return `${file.name || 'This image'} must be ${formatFileSize(MAX_IMAGE_BYTES)} or smaller.`;
      }
    }

    return '';
  }

  function showStatus(form, type, message) {
    const status = form.querySelector('[data-role="backend-form-status"]');
    if (!status) return;
    status.hidden = false;
    status.dataset.statusType = type;
    status.className = `wl-form-upload-status wl-form-upload-status--${type}`;
    status.textContent = message;
  }

  function renderFileList(form) {
    const list = form.querySelector('[data-role="backend-file-list"]');
    if (!list) return;

    const files = getFiles(form);
    if (!files.length) {
      list.hidden = true;
      list.innerHTML = '';
      return;
    }

    list.hidden = false;
    list.innerHTML = files
      .map(
        (file) => `
          <li>
            <span>${escapeHtml(file.name || 'File')}</span>
            <small>${escapeHtml(formatFileSize(file.size || 0))}</small>
          </li>
        `
      )
      .join('');

    const validationError = validateFiles(form);
    if (validationError) {
      list.insertAdjacentHTML('beforeend', `<li class="wl-form-upload-files__error">${escapeHtml(validationError)}</li>`);
    }
  }

  function getFieldValue(form, selector) {
    return (form.querySelector(selector)?.value || '').trim();
  }

  function getContactFieldEntries(form) {
    const fields = {};
    const formData = new FormData(form);
    for (const [key, value] of formData.entries()) {
      if (value instanceof File) continue;
      const normalizedKey = String(key).replace(/^contact\[/, '').replace(/\]$/, '');
      fields[normalizedKey] = String(value || '').trim();
    }
    return fields;
  }

  function buildPayload(form) {
    const submissionType = form.dataset.submissionType || 'contact';
    const fields = getContactFieldEntries(form);
    const name = fields.name || getFieldValue(form, '[name="contact[name]"]');
    const email = fields.email || getFieldValue(form, '[name="contact[email]"]');
    const phone = fields.phone || getFieldValue(form, '[name="contact[phone]"]');
    const message = fields.body || getFieldValue(form, '[name="contact[body]"]');
    const company = fields['Company/Organization Name'] || '';
    const state = fields.State || '';

    return {
      schema_version: 1,
      request_type: submissionType === 'tax_exemption' ? 'tax_exemption_request' : 'contact_message',
      submission_type: submissionType,
      request_id: `${submissionType}-${window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`}`,
      generated_at: new Date().toISOString(),
      source_url: window.location.href,
      customer: {
        name,
        email,
        phone,
      },
      company,
      state,
      message,
      fields,
      files: getFiles(form).map((file) => ({
        name: file.name || 'File',
        size: formatFileSize(file.size || 0),
        size_bytes: file.size || 0,
        type: file.type || getExtension(file.name),
      })),
    };
  }

  async function submitForm(form) {
    const endpoint = (form.dataset.endpoint || '').trim();
    const submitButton = form.querySelector('[type="submit"]');
    const validationError = validateFiles(form);

    if (!endpoint) {
      showStatus(form, 'error', 'This form is missing its backend endpoint.');
      return;
    }

    if (validationError) {
      showStatus(form, 'error', validationError);
      return;
    }

    if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

    const payload = buildPayload(form);
    const body = new FormData();
    body.set('payload', JSON.stringify(payload));
    getFiles(form).forEach((file) => {
      body.append('files', file, file.name || 'upload');
    });

    const previousText = submitButton?.textContent || '';
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Sending...';
    }
    showStatus(form, 'info', 'Sending...');

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        body,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.ok) {
        throw new Error(result.error || `Backend returned ${response.status}`);
      }

      form.reset();
      renderFileList(form);
      showStatus(form, 'success', form.dataset.successMessage || 'Your submission was received.');
    } catch (error) {
      console.error(error);
      showStatus(form, 'error', error.message || 'Could not send this form. Please try again.');
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = previousText;
      }
    }
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  document.addEventListener('change', (event) => {
    const input = event.target.closest('form[data-backend-submission] input[type="file"]');
    if (!input) return;
    renderFileList(input.form);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-backend-submission]');
    if (!form) return;
    event.preventDefault();
    submitForm(form);
  });
})();
